import {
  DESCRIPTION_EXPANDED_KEY,
  DESCRIPTION_KEY,
  FUNCTIONAL_KEY,
  NAME_KEY,
  PAGES_KEY,
  PROPERTY_NAME,
  PROPERTY_VALUE,
  QUESTION_EXPANDED_KEY,
  TITLE_KEY,
  TYPE_KEY,
  VISIBLE_KEY
} from "../common/constant";
import { CHANGE_LANGUAGE_ACTION, LANGUAGE } from "./languageSelector";
import { CREATE_NEW_ACTION, LOAD_SUCCESSFUL_ACTION, SURVEY_RESET_PAGE, SURVEY_TEMPLATE } from "./survey";
import {
  ADD_INDICATOR_TO_QUESTION,
  ADD_PAGE_ACTION,
  IMPORT_LIBRARY_SECTION,
  IMPORT_LIBRARY_SECTIONS,
  IMPORT_LIBRARY_QUESTIONS,
  ADD_QUESTION_ACTION,
  ADD_QUESTION_CHOICE_ACTION,
  CANCEL_SUBCHOICE_ACTION,
  CHANGE_QUESTION_FIELD,
  CHANGE_SELECTED_PAGE,
  CONFIRM_SUBCHOICE_ACTION,
  COPY_QUESTION_ACTION,
  EDIT_PAGE_ACTION,
  EDIT_SUBCHOICE_ACTION,
  FILTER_INVISIBLE,
  MOVE_QUESTION_TO_PAGE,
  PREPARE_DATA_FOR_EDITING_PAGE_ACTION,
  PROPERTY_CHANGED_ACTION,
  REMOVE_INDICATOR_FROM_QUESTION,
  REMOVE_PAGE_ACTION,
  REMOVE_QUESTION_ACTION,
  REMOVE_QUESTION_CHOICE_ACTION,
  REORDER_CHOICE,
  REORDER_PAGE,
  REORDER_QUESTION,
  RESET_PAGES_ACTION,
  SET_INDICATORS_LIST_ACTION,
  SET_PREDEFINED_LIST_ACTION,
  SET_TRIGGERS,
  SET_VALIDATOR,
  SET_VALIDATOR_PROPERTY
} from "./surveyEditorActions";

import configFunctionalKey from "../common/configFunctionalKey";
import { copyLibrarySection } from "../utils/SectionLibraryUtils";
import deConfig from "../i18n/configDe";
import enConfig from "../i18n/configEn";
import ObjectUtils, { coalesce } from "../utils/ObjectUtils";
import ValidatorUtils, { VALIDATOR_TYPE } from "../utils/ValidatorUtils";
import SurveyUtils from "./../utils/SurveyUtils";
import { clearMessages } from "./message";

const { filterExcludeByTemplate } = ObjectUtils;
export const CONFIG_PROPS = "configProps";
export const FUNCTIONAL_KEY_PROPS = "functionalKeyProps";
const configs = {
  de: deConfig,
  en: enConfig
};

export const QUESTIONS = "questions";
export const PAGE_NAME = "pageName";
export const QUESTION_TYPE = "questionTypes";
export const QUESTION_TO_COPY = "questionToCopy";
export const OLD_PAGE_TITLE_KEY = "oldPageTitle";
export const FIELD_NAME = "fieldName";
export const FIELD_VALUE = "fieldValue";
export const CHOICE_NAME = "choiceName";
export const RESET_SURVEY_ACTION = "SURVEY_EDITOR.RESET_SURVEY_ACTION";
const TEMP_CHOICE_TEXT = "tempChoiceText";
const TEMP_CHOICE_TYPE = "tempChoiceType";
const TEMP_SUFFIX = "tempSuffix";
const CHOICE_DESCRIPTION = "choiceDescription";
export const CHOICE_TARGET = "choiceTarget";
export const CHOICE_CHOICE_NAME = "choiceChoiceName";
export const FIELD_REQUIRED = "isRequired";
export const EVALUATE_SINCE_FUNDING_STARTED = "evaluateSinceFundingStarted";
export const LOAD_PREVIOUS_DATA_ENABLED = "loadPreviousDataEnabled";
export const UPLOAD_ENABLED = "enableUpload";
export const HAS_AN_INDICATOR = "hasAnIndicator";
export const OTHER_OPTION = "hasOther";
export const PREDEFINED_LIST = "predefinedList";
export const PREDEFINED_LIST_TYPE = "predefinedListType";
export const TEMP_OTHER_OPTION = "tempHasOther";
const CHOICE_EXCLUSIVE = "choiceExclusive";
const TEMP_CHOICE_EXCLUSIVE = "tempChoiceExclusive";
export const VALIDATOR = "validator";
export const VALIDATORS = "validators";
export const TARGET_VALIDATORS = "targetValidators";
export const COLUMN_NAME = "columnName";
export const GENERATE_NEW_OPTIONS = "generateNewOptions";
const RADIO = "radio";
const DROPDOWN = "dropdown";
const CHECKBOX = "checkbox";
const MATRIX_SINGLE_CHOICE_TYPE = "matrixSingleChoice";
const STATIC_TABLE_TYPE = "staticTable";
const DYNAMIC_TABLE_TYPE = "dynamicTable";
export const ALL_INDICATORS = "allIndicators";
export const INDICATORS = "indicators";
export const INDICATOR_TO_BE_REPLACED = "indicatorToBeReplaced";
export const SHOW_MODAL_PAGE_EDITOR = "showModalPageEditor";
export const EDITING_PAGE_TITLE = "editingPageTitle";
export const EDITING_PAGE_KEY = "editingPageKey";
export const EDITING_PAGE_DESCRIPTION = "editingPageDescription";
export const EDITING_PAGE_VISIBILITY = "editingPageVisibility";

/**
 * Deliberately exported for testing purposes.
 */
export const initialState = {
  [LANGUAGE]: "de",
  [CONFIG_PROPS]: configs["de"],
  [FUNCTIONAL_KEY_PROPS]: configFunctionalKey,
  [PAGES_KEY]: [
    {
      [NAME_KEY]: configs["de"]["page"] + "1",
      [DESCRIPTION_KEY]: "",
      [FUNCTIONAL_KEY]: "page_1",
      [QUESTIONS]: [],
      [VISIBLE_KEY]: true
    }
  ],
  pageCounter: 1,
  questionCounter: 0,
  choiceCounter: 0,
  rowCounter: 0,
  selectedPage: configs["de"]["page"] + "1",
  invisible: [],
  fieldRequired: false,
  otherOption: false,
  [SHOW_MODAL_PAGE_EDITOR]: ""
};

export default function reducer(state = initialState, action) {
  let currentState = ObjectUtils.deepClone(state);
  let actionType = action[TYPE_KEY];

  switch (actionType) {
    case RESET_SURVEY_ACTION:
      currentState = Object.assign({}, state, initialState);
      break;
    case CHANGE_LANGUAGE_ACTION:
      currentState[LANGUAGE] = action[LANGUAGE];
      currentState[CONFIG_PROPS] = configs[action[LANGUAGE]];
      break;
    case ADD_QUESTION_ACTION:
      handleAddQuestionAction(currentState, action);
      break;
    case COPY_QUESTION_ACTION:
      handleCopyQuestionAction(currentState, action);
      break;
    case REMOVE_QUESTION_ACTION:
      handleRemoveQuestionAction(currentState, action);
      break;
    case ADD_PAGE_ACTION:
      handleAddPageAction(currentState, action);
      break;
    case IMPORT_LIBRARY_SECTION:
    case IMPORT_LIBRARY_SECTIONS: {
      const sections = action.type === IMPORT_LIBRARY_SECTION
        ? [{ section: action.section, id: action.sectionLibraryId }]
        : action.sections;
      for (const entry of sections) {
        const section = copyLibrarySection(entry.section, currentState.pages);
        section.sectionLibraryId = entry.id;
        currentState.pages.push(section);
        currentState.selectedPage = section.name;
      }
      break;
    }
    case IMPORT_LIBRARY_QUESTIONS: {
      const page = currentState.pages.find(page => page.name === action.pageName);
      if (!page) return state;
      for (const libraryQuestion of action.libraryQuestions) {
        const question = ObjectUtils.deepClone(libraryQuestion.question);
        question.name = buildQuestionName(currentState);
        question.title = libraryQuestion.name;
        question.libraryQuestionId = libraryQuestion.id;
        page.questions.push(question);
      }
      break;
    }
    case EDIT_PAGE_ACTION:
      handleEditPageAction(currentState, action);
      break;
    case PREPARE_DATA_FOR_EDITING_PAGE_ACTION:
      currentState = Object.assign({}, state, {
        [SHOW_MODAL_PAGE_EDITOR]: action[SHOW_MODAL_PAGE_EDITOR],
        [EDITING_PAGE_TITLE]: action[EDITING_PAGE_TITLE],
        [EDITING_PAGE_KEY]: action[EDITING_PAGE_KEY],
        [EDITING_PAGE_DESCRIPTION]: action[EDITING_PAGE_DESCRIPTION],
        [EDITING_PAGE_VISIBILITY]: action[EDITING_PAGE_VISIBILITY]
      });
      break;
    case REMOVE_PAGE_ACTION:
      handleRemovePageAction(currentState, action);
      break;
    case CREATE_NEW_ACTION:
    case LOAD_SUCCESSFUL_ACTION:
      handleLoadTemplate(currentState, action);
      break;
    case CHANGE_QUESTION_FIELD:
      handleChangeQuestionField(currentState, action);
      break;
    case ADD_QUESTION_CHOICE_ACTION:
      handleAddQuestionChoiceAction(currentState, action);
      break;
    case SET_PREDEFINED_LIST_ACTION:
      handleAddPredefinedQuestionChoiceAction(currentState, action);
      break;
    case REMOVE_QUESTION_CHOICE_ACTION:
      handleRemoveQuestionChoiceAction(currentState, action);
      break;
    case EDIT_SUBCHOICE_ACTION:
      handleEditSubchoiceAction(currentState, action);
      break;
    case CONFIRM_SUBCHOICE_ACTION:
      handleConfirmSubchoiceAction(currentState, action);
      break;
    case CANCEL_SUBCHOICE_ACTION:
      handleCancelSubchoiceAction(currentState, action);
      break;
    case CHANGE_SELECTED_PAGE:
      handleChangeSelectedPage(currentState, action);
      break;
    case REORDER_QUESTION:
      handleReorderQuestion(currentState, action);
      break;
    case REORDER_CHOICE:
      handleReorderChoice(currentState, action);
      break;
    case REORDER_PAGE:
      handleReorderPage(currentState, action);
      break;
    case MOVE_QUESTION_TO_PAGE:
      handleMoveQuestionToPage(currentState, action);
      break;
    case FILTER_INVISIBLE:
      handleFilterInvisible(currentState);
      break;
    case SET_TRIGGERS:
      handleSetTriggers(currentState, action);
      break;
    case RESET_PAGES_ACTION:
      handleReset(currentState, action);
      break;
    case SET_VALIDATOR:
      handleSetValidator(currentState, action);
      break;
    case SET_VALIDATOR_PROPERTY:
      handleSetValidatorProperty(currentState, action);
      break;
    case ADD_INDICATOR_TO_QUESTION:
      handleChangeOfIndicatorInQuestion(currentState, action, true);
      break;
    case REMOVE_INDICATOR_FROM_QUESTION:
      handleChangeOfIndicatorInQuestion(currentState, action, false);
      break;
    case SET_INDICATORS_LIST_ACTION:
      currentState = Object.assign({}, state, {
        [ALL_INDICATORS]: action[FIELD_VALUE]
      });
      break;
    case PROPERTY_CHANGED_ACTION:
      currentState = Object.assign({}, state, {
        [action[PROPERTY_NAME]]: action[PROPERTY_VALUE]
      });
      break;
    default:
      return state;
  }

  return currentState;
}

export function resetSurvey() {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: RESET_SURVEY_ACTION
    });
    dispatch(clearMessages());
  };
}

export function isQuestionKeyDuplicated(currentState, questionKey) {
  const questions = getQuestionsFromSurvey(currentState);
  return questions.filter(q => q.key === questionKey).length > 1;
}

export function isPageKeyDuplicated(currentState, pageKey) {
  const pages = currentState?.pages || [];
  return pages.filter(page => page.key === pageKey).length > 1;
}

export function hasSurveyDuplicateQuestionKeys(surveyTemplate) {
  const questions = getQuestionsFromSurvey(surveyTemplate);
  for (const question of questions) {
    if (questions.filter(q => q.key === question.key).length > 1) {
      return true
    }
  }
  return false;
}

export function hasSurveyDuplicatePageKeys(surveyTemplate) {
  const pages = surveyTemplate?.pages || [];
  for (const page of pages) {
    if (pages.filter(p => p.key === page.key).length > 1) {
      return true;
    }
  }
  return false;
}

export function getQuestionsFromSurvey(survey) {
  let questions = [];
  if (survey) {
    for (const page of survey.pages) {
      questions = [...questions, ...page.questions];
    }
  }
  return questions;
}

function handleReset(currentState, action) {
  let pages = action[PAGES_KEY];
  const pageName = currentState[CONFIG_PROPS]["page"] + "1";
  pages.push({
    [NAME_KEY]: pageName,
    [DESCRIPTION_KEY]: "",
    [QUESTIONS]: [],
    [FUNCTIONAL_KEY]: "page_1",
    [VISIBLE_KEY]: true
  });
  currentState["selectedPage"] = pageName;
  currentState[PAGES_KEY] = pages;
}

function handleSetTriggers(currentState, action) {
  let question = getCurrentQuestion(currentState, action);
  question.triggers = action.triggers;
}

function handleFilterInvisible(currentState) {
  let invisible = [];

  for (let page of currentState.pages) {
    invisible.push({
      ...page,
      triggerTargetType: "page",
      visibleTriggerTarget: page.visible !== false
    });
    for (let question of page.questions) {
      invisible.push({
        ...question,
        triggerTargetType: "question",
        visibleTriggerTarget: question.visible !== false
      });
    }
  }
  currentState.invisible = invisible;
}

function handleReorderChoice(currentState, action) {
  let currentQuestion = getCurrentQuestion(currentState, action);
  let choiceTarget = coalesce(action[CHOICE_TARGET], "choices");
  swapPositions(currentQuestion[choiceTarget], action.dragged, action.toBeReplaced);
}

function handleReorderPage(currentState, action) {
  swapPositions(currentState.pages, action.dragged, action.toBeReplaced);
}

function handleReorderQuestion(currentState, action) {
  let currentPage = getCurrentPage(currentState, action);
  swapPositions(currentPage.questions, action.dragged, action.toBeReplaced);
}

function handleMoveQuestionToPage(currentState, action) {
  let currentPage = ObjectUtils.findByObjectProp(currentState.pages, [NAME_KEY], currentState.selectedPage);
  let currentQuestion = ObjectUtils.findByObjectProp(currentPage.questions, [NAME_KEY], action.questionToBeMoved);
  let targetPage = ObjectUtils.findByObjectProp(currentState.pages, [NAME_KEY], action.targetPage);
  targetPage.questions.push(currentQuestion);
  currentPage.questions = ObjectUtils.removeObjectByProp(currentPage.questions, [NAME_KEY], action.questionToBeMoved);
}

function swapPositions(collection, nameOfMovedObj, nameOfTargetObj) {
  let oldIndex = ObjectUtils.findObjectIndexInArray(collection, [NAME_KEY], nameOfMovedObj);
  //backward compatibility
  if (oldIndex === -1) {
    oldIndex = ObjectUtils.findObjectIndexInArray(collection, "value", nameOfMovedObj);
  }
  let indexToTake = ObjectUtils.findObjectIndexInArray(collection, [NAME_KEY], nameOfTargetObj);
  //backward compatibility
  if (indexToTake === -1) {
    indexToTake = ObjectUtils.findObjectIndexInArray(collection, "value", nameOfTargetObj);
  }

  let i;
  if (indexToTake > oldIndex) {
    let temp = collection[oldIndex];
    i = oldIndex + 1;
    for (i; i <= indexToTake; i++) {
      let j = i - 1;
      collection[j] = collection[i];
    }
    collection[indexToTake] = temp;
  }
  if (indexToTake < oldIndex) {
    let temp = collection[oldIndex];
    i = oldIndex - 1;
    for (i; i >= indexToTake; i--) {
      let k = i + 1;
      collection[k] = collection[i];
    }
    collection[indexToTake] = temp;
  }
}

function handleChangeSelectedPage(currentState, action) {
  currentState.selectedPage = action.selectedPage;
}

function handleLoadTemplate(currentState, action) {
  let pages = action[SURVEY_TEMPLATE][PAGES_KEY];
  if (pages) {
    currentState[PAGES_KEY] = pages;
    if (coalesce(action[SURVEY_RESET_PAGE], true)) {
      currentState.selectedPage = coalesce(ObjectUtils.getDeepProperty(pages, 0, "name"), "");
    }
  }
}

function uniqueEntityName(namePrefix, nameIndex) {
  let qName;
  for (let counter = 1; counter < Number.MAX_SAFE_INTEGER; counter++) {
    qName = namePrefix + counter;
    // if qName doesn't exist in the question index, we've reached non-existing question name;
    if (!nameIndex[qName]) {
      break;
    }
  }

  return qName;
}

/**
 * generates a unique key using the entity counters in the state.
 *
 * @param currentState the current state object
 * @param entityIndex the index of all entities to look up keys for
 * @param keyType type of the key e.g. "question"
 * @param dataType the dataType of the entity e.g. "staticTable"
 * @returns {string} the compiled unique key for the entity
 */
function uniqueKey(currentState, entityIndex, keyType, dataType) {
  let prefix = dataType
    ? `${coalesce(ObjectUtils.getDeepProperty(currentState, FUNCTIONAL_KEY_PROPS, keyType), "")}_${dataType}_`
    : `${coalesce(ObjectUtils.getDeepProperty(currentState, FUNCTIONAL_KEY_PROPS, keyType), "")}_`;
  return uniqueEntityName(prefix, entityIndex);
}

export function indexQuestionByProperty(currentState, identifier) {
  let index = {};
  let pages = currentState[PAGES_KEY];
  for (let i = 0; i < pages.length; i++) {
    let page = pages[i];
    let questions = page.questions;
    for (let j = 0; j < questions.length; j++) {
      let question = questions[j];
      index[identifier ? question[identifier] : question.name] = {
        [PAGE_NAME]: page.name,
        pageIndex: i,
        questionIndex: j,
        questionTitle: question.title
      };
    }
  }
  return index;
}

function indexPagesByProperty(currentState, identifier) {
  let index = {};
  let pages = currentState[PAGES_KEY];
  for (let i = 0; i < pages.length; i++) {
    let page = pages[i];
    index[identifier ? page[identifier] : page.name] = {
      [PAGE_NAME]: page.name,
      pageIndex: i
    };
  }
  return index;
}

function indexChoicesByProperty(currentQuestion, type, identifier) {
  let index = {};
  for (let i = 0; i < currentQuestion[type]?.length; i++) {
    index[identifier ? currentQuestion[type][i][identifier] : currentQuestion[type][i].name] = {
      name: currentQuestion[type][i].name
    };
  }
  return index;
}

function handleAddQuestionAction(currentState, action) {
  let currentPage = getCurrentPage(currentState, action);

  // Prevent adding more than one SAP project on same question
  if (action.questionTypes === "projectData") {
    for (let i = 0; i < currentState.pages.length; i++) {
      for (let j = 0; j < currentState.pages[i].questions.length; j++) {
        if (currentState.pages[i].questions[j]["type"] === "projectData") {
          return false;
        }
      }
    }
  }

  let prefix = coalesce(ObjectUtils.getDeepProperty(currentState, CONFIG_PROPS, "question"), "...");
  let nameIndex = indexQuestionByProperty(currentState);
  let keyIndex = indexQuestionByProperty(currentState, "key");
  let questionName = uniqueEntityName(prefix, nameIndex);
  let questionKey = uniqueKey(currentState, keyIndex, "questions", action[QUESTION_TYPE]);
  let isRequired = !(action[QUESTION_TYPE] === "infoMessage" || action[QUESTION_TYPE] === "projectData");
  let question = {
    [TYPE_KEY]: action[QUESTION_TYPE],
    [NAME_KEY]: questionName,
    [TITLE_KEY]: questionName,
    [DESCRIPTION_KEY]: "",
    [QUESTION_EXPANDED_KEY]: false,
    [DESCRIPTION_EXPANDED_KEY]: false,
    [VISIBLE_KEY]: true,
    [FIELD_REQUIRED]: isRequired,
    triggers: [],
    [PREDEFINED_LIST_TYPE]: "countries",
    [INDICATORS]: [],
    [FUNCTIONAL_KEY]: questionKey
  };

  if (
    isMultiChoiceQuestionType(action[QUESTION_TYPE]) ||
    isSingleChoiceMatrixType(action[QUESTION_TYPE]) ||
    isTableQuestionType(action[QUESTION_TYPE])
  ) {
    let target = isTableQuestionType(action[QUESTION_TYPE]) ? "columns" : "choices";
    question[target] = generateOption(
      coalesce(ObjectUtils.getDeepProperty(currentState, CONFIG_PROPS, target), "Option-"),
      2,
      currentState,
      question,
      target
    );
    isTableQuestionType(action[QUESTION_TYPE]) && question[target].forEach((element) => {
      element.type = "Dropdown";
      element.choices = generateOption(
        coalesce(ObjectUtils.getDeepProperty(currentState, CONFIG_PROPS, "choices"), "Option-"),
        2,
        currentState,
        question,
        "choices"
      );
      // alter key with correct datatype except if its a matrixSinglechoice
      if (!isSingleChoiceMatrixType(action[QUESTION_TYPE])) {
        let keyParts = element[FUNCTIONAL_KEY].split("_");
        element[FUNCTIONAL_KEY] = `${keyParts[0]}_${element.type}_${keyParts[keyParts.length - 1]}`;
      }
    });
  }

  if (isSingleChoiceMatrixType(action[QUESTION_TYPE]) || isTableQuestionType(action[QUESTION_TYPE])) {
    question.rows = generateOption(
      coalesce(ObjectUtils.getDeepProperty(currentState, CONFIG_PROPS, "rows"), "Option-"),
      2,
      currentState,
      question,
      "rows"
    );
  }

  currentPage.questions.push(question);
}

export function buildQuestionName(currentState) {
  let prefix = coalesce(ObjectUtils.getDeepProperty(currentState, CONFIG_PROPS, "question"), "...");
  let nameIndex = indexQuestionByProperty(currentState);
  let questionName = uniqueEntityName(prefix, nameIndex);

  return questionName;
}

function handleCopyQuestionAction(currentState, action) {
  let currentPage = getCurrentPage(currentState, action);

  let questionName = buildQuestionName(currentState);

  // take position
  let indexOfCopyedQuestion =
    ObjectUtils.findObjectIndexInArray(currentPage.questions, [NAME_KEY], action.questionToCopy.name) + 1;

  let sufix = coalesce(ObjectUtils.getDeepProperty(currentState, CONFIG_PROPS, "copy"), "...");
  let question = action.questionToCopy;
  question.title = action.questionToCopy.title + " " + sufix;
  question.name = questionName;
  let questionIndex = indexQuestionByProperty(currentState, "key");
  let questionKey = uniqueKey(currentState, questionIndex, "questions", action.questionToCopy?.type);
  question[FUNCTIONAL_KEY] = questionKey;
  delete question.libraryQuestionId;

  //changing positions
  let i = currentPage.questions.length - 1;
  for (i; i >= indexOfCopyedQuestion; i--) {
    let k = i + 1;
    currentPage.questions[k] = currentPage.questions[i];
  }
  currentPage.questions[indexOfCopyedQuestion] = question;
}

function generateOption(prefix, count, currentState, question, type) {
  let result = [];
  let optionIndex = indexChoicesByProperty(question, type, "key");
  for (let i = 0; i < count; i++) {
    let text = prefix + i;
    let choiceKey = uniqueKey(currentState, optionIndex, type, null);
    result.push({
      name: text,
      value: text,
      text: "",
      description: "description",
      choiceDescriptionVisible: false,
      editable: true,
      [FUNCTIONAL_KEY]: choiceKey
    });
    optionIndex[choiceKey] = { name: text };
  }
  return result;
}

function isMultiChoiceQuestionType(questionType) {
  return CHECKBOX === questionType;
}

function isSingleChoiceMatrixType(questionType) {
  return RADIO === questionType || DROPDOWN === questionType || MATRIX_SINGLE_CHOICE_TYPE === questionType;
}

function isTableQuestionType(questionType) {
  return STATIC_TABLE_TYPE === questionType || DYNAMIC_TABLE_TYPE === questionType;
}

function handleRemoveQuestionAction(currentState, action) {
  let currentPage = getCurrentPage(currentState, action);

  currentPage.questions = ObjectUtils.removeObjectByProp(currentPage.questions, [NAME_KEY], action.name);
}

function handleAddPageAction(currentState, action) {
  let prefix = coalesce(ObjectUtils.getDeepProperty(currentState, CONFIG_PROPS, "page"), "...");
  let newPageName = uniquePageName(currentState.pages, prefix);
  let pageIndex = indexPagesByProperty(currentState, "key");
  let pageKey = uniqueKey(currentState, pageIndex, "pages", null);

  const pageFromTemplate = action.page !== undefined;
  if (pageFromTemplate) {
    currentState[PAGES_KEY].push({
      [NAME_KEY]: newPageName,
      [TITLE_KEY]: newPageName,
      [DESCRIPTION_KEY]: action.page.description,
      [VISIBLE_KEY]: action.page.visible,
      [QUESTIONS]: action.page.questions,
      [FUNCTIONAL_KEY]: pageKey
    });
    currentState.questionCounter = action.page.questions.length;
  } else {
    currentState[PAGES_KEY].push({
      [NAME_KEY]: newPageName,
      [TITLE_KEY]: newPageName,
      [DESCRIPTION_KEY]: "",
      [VISIBLE_KEY]: true,
      [QUESTIONS]: [],
      [FUNCTIONAL_KEY]: pageKey
    });
  }

  currentState.selectedPage = newPageName;
}

function uniquePageName(pages, namePrefix) {
  let newPageName;
  for (let counter = 1; counter < Number.MAX_SAFE_INTEGER; counter++) {
    newPageName = namePrefix + counter;
    // checking is there page with this name
    if (ObjectUtils.findObjectIndexInArray(pages, "name", newPageName) < 0) {
      break;
    }
  }

  return newPageName;
}

function handleEditPageAction(currentState, action) {
  let allPages = currentState[PAGES_KEY];

  let page = ObjectUtils.findByObjectProp(allPages, [NAME_KEY], action.selectedPage);
  page.title = action[EDITING_PAGE_TITLE];
  page.key = action[EDITING_PAGE_KEY];
  page.description = action[EDITING_PAGE_DESCRIPTION];
  page.visible = action[EDITING_PAGE_VISIBILITY];
  currentState.selectedPage = action.selectedPage;
}

function handleRemovePageAction(currentState, action) {
  let allPages = currentState[PAGES_KEY];
  let pageName = action[PAGE_NAME];

  if (allPages.length === 1) {
    throw Error("Cannot delete the only page.");
  }

  currentState.pages = ObjectUtils.removeObjectByProp(allPages, [NAME_KEY], pageName.selectedPage);
  currentState.selectedPage = currentState.pages[0].name;
}

function handleChangeQuestionField(currentState, action) {
  if (action[CHOICE_NAME]) {
    let currentQuestion = getCurrentQuestion(currentState, action);
    // if we are changing type of subquestion we need to remove validator for it too
    if ("type" === action[FIELD_NAME] && currentQuestion.columns) {
      let subQuestion =
        currentQuestion.columns[
          ObjectUtils.findObjectIndexInArray(currentQuestion.columns, "name", action[CHOICE_NAME])
        ];
      if (subQuestion && subQuestion.type !== action[FIELD_VALUE] && currentQuestion.validators) {
        let validatorToRemove = ObjectUtils.findObjectIndexInArray(
          currentQuestion.validators,
          "columnName",
          action[CHOICE_NAME]
        );
        if (validatorToRemove >= 0) {
          currentQuestion.validators.splice(validatorToRemove, 1);
        }
      }
    }

    let choice = getCurrentChoice(currentState, action);
    choice[action[FIELD_NAME]] = action[FIELD_VALUE];
    if ("type" === action[FIELD_NAME]) {
      let keyIndex = indexChoicesByProperty(currentQuestion, action[CHOICE_TARGET], "key");
      let choiceKey = uniqueKey(currentState, keyIndex, action[CHOICE_TARGET], action[FIELD_VALUE]);
      choice[FUNCTIONAL_KEY] = choiceKey;
      if (action[FIELD_VALUE] === "Checkbox" || action[FIELD_VALUE] === "Radio") {
        if (!choice.choices || choice.choices.length === 0) {
          choice.choices = generateOption(
            coalesce(ObjectUtils.getDeepProperty(currentState, CONFIG_PROPS, "choices"), "Option-"),
            2,
            currentState,
            currentQuestion,
            "choices"
          );
        }
      } else {
        choice.choices = [];
      }
    }
  } else {
    let currentQuestion = getCurrentQuestion(currentState, action);
    currentQuestion[action[FIELD_NAME]] = action[FIELD_VALUE];
    if ("type" === action[FIELD_NAME]) {
      handleChangeQuestionType(currentState, currentQuestion, action[FIELD_VALUE]);
    } else if ("description" === action[FIELD_NAME]) {
      currentQuestion[action[DESCRIPTION_EXPANDED_KEY]] = true;
    }
  }
}

function handleAddPredefinedQuestionChoiceAction(currentState, action) {
  let language = currentState.language === "de" ? "germanName" : "englishName";
  let currentQuestion = getCurrentQuestion(currentState, action);
  if (currentQuestion["tempChoices"]) {
    currentQuestion["tempChoices"].length = 0;
    currentQuestion.predefinedListType = action[PREDEFINED_LIST_TYPE];
    for (let i = 0; i < action[FIELD_VALUE].length; i++) {
      let newOption = {
        name: action[FIELD_VALUE][i].id,
        value: action[FIELD_VALUE][i].id,
        text: action[FIELD_VALUE][i][language],
        description: "description",
        choiceDescriptionVisible: false
      };
      currentQuestion["tempChoices"].push(newOption);
    }
    currentQuestion["tempChoices"] = SurveyUtils.handleSortTemplateList(currentQuestion["tempChoices"], "text", true);
  } else {
    currentQuestion["choices"].length = 0;
    for (let i = 0; i < action[FIELD_VALUE].length; i++) {
      let newOption = {
        name: action[FIELD_VALUE][i].id,
        value: action[FIELD_VALUE][i].id,
        text: action[FIELD_VALUE][i][language],
        description: "description",
        choiceDescriptionVisible: false
      };
      currentQuestion["choices"].push(newOption);
    }
    currentQuestion["choices"] = SurveyUtils.handleSortTemplateList(currentQuestion["choices"], "text", true);
    currentQuestion[PREDEFINED_LIST_TYPE] = action[PREDEFINED_LIST_TYPE];
  }
}

function handleChangeQuestionType(currentState, question, newType) {
  switch (newType) {
    case "dynamicTable":
      question.rows.length = 0;
      break;
    case "staticTable":
      question.rows = generateOption(
        coalesce(ObjectUtils.getDeepProperty(currentState, CONFIG_PROPS, "rows"), "Option-"),
        2,
        currentState,
        question,
        "rows"
      );
      break;
    default:
      break;
  }

  let keyIndex = indexQuestionByProperty(currentState, "key");
  let questionKey = uniqueKey(currentState, keyIndex, "questions", newType);
  question[FUNCTIONAL_KEY] = questionKey;
}

function handleSetValidator(currentState, action) {
  let currentQuestion = getCurrentQuestion(currentState, action);
  if (currentQuestion) {
    let target = coalesce(action[TARGET_VALIDATORS], VALIDATORS);
    let validators = currentQuestion[target];
    if (!validators) {
      validators = [];
    }
    validators = filterExcludeByTemplate(validators, { [COLUMN_NAME]: action[COLUMN_NAME] });
    let validator = action[VALIDATOR];
    if (action[COLUMN_NAME]) {
      validator[COLUMN_NAME] = action[COLUMN_NAME];
    }
    if (validator.validatorType === "length" && validator.props.length === "") {
      validators.splice(0, 1);
    } else {
      validators.push(validator);
    }
    currentQuestion[target] = validators;
  }
}

function handleSetValidatorProperty(currentState, action) {
  let currentQuestion = getCurrentQuestion(currentState, action);
  let date = ["Year", "Month", "Day"];
  if (currentQuestion) {
    if (date.includes(action.fieldValue)) {
      currentQuestion["dateFormat"] = action.fieldValue;
      return true;
    }
    let target = coalesce(action[TARGET_VALIDATORS], VALIDATORS);
    let validators = currentQuestion[target];
    if (!validators) {
      validators = [];
    }
    const type = action[VALIDATOR_TYPE];

    // if there is COLUMN_NAME - question is type of table- so we should find validator by question name, otherwise we are looking for it by validator type
    let validator = action[COLUMN_NAME]
      ? validators[ObjectUtils.findObjectIndexInArray(validators, COLUMN_NAME, action[COLUMN_NAME])]
      : validators[ObjectUtils.findObjectIndexInArray(validators, VALIDATOR_TYPE, type)];
    if (!validator || !validator.props) {
      validator = ValidatorUtils.createValidator(type);
      if (action[COLUMN_NAME]) {
        validator[COLUMN_NAME] = action[COLUMN_NAME];
      }
      validators.push(validator);
    }

    if (action[FIELD_VALUE] === "") {
      delete validator.props[action[FIELD_NAME]];
    } else {
      validator.props[action[FIELD_NAME]] = action[FIELD_VALUE];
    }

    currentQuestion[target] = validators;
  }
}

function handleAddQuestionChoiceAction(currentState, action) {
  let currentQuestion = getCurrentQuestion(currentState, action);
  let choiceTarget = coalesce(action.choiceTarget, "choices");

  let prefix = coalesce(ObjectUtils.getDeepProperty(currentState, CONFIG_PROPS, choiceTarget), "Option-");
  if (action.generateNewOptions) {
    currentQuestion[choiceTarget] = [];
    currentQuestion[choiceTarget] = generateOption(
      coalesce(ObjectUtils.getDeepProperty(currentState, CONFIG_PROPS, "choices"), "Option-"),
      2,
      currentState,
      currentQuestion,
      "choices"
    );
  } else {
    let nameIndex = indexChoicesByProperty(currentQuestion, choiceTarget);
    let choiceIndex = indexChoicesByProperty(currentQuestion, choiceTarget, "key");
    let choiceName = uniqueEntityName(prefix, nameIndex);
    let choiceKey = uniqueKey(currentState, choiceIndex, choiceTarget, choiceTarget === "columns" ? "Text" : null);
    currentQuestion[choiceTarget].push({
      dateFormat: choiceTarget,
      [NAME_KEY]: choiceName,
      value: choiceName,
      text: "",
      description: "description",
      type: "Text",
      choices: [],
      choiceDescriptionVisible: false,
      editable: true,
      required: false,
      [FUNCTIONAL_KEY]: choiceKey
    });
  }
}

function handleEditSubchoiceAction(currentState, action) {
  let currentQuestion = getCurrentQuestion(currentState, action);
  const target = action.choiceTarget;
  const choiceName = action.choiceName;
  currentQuestion.editSubchoice = choiceName;
  let choice = ObjectUtils.findByObjectProp(currentQuestion[target], NAME_KEY, choiceName);
  let currSubchoice = coalesce(choice.choices, []);
  currentQuestion.tempChoices = currSubchoice;
  choice[TEMP_SUFFIX] = coalesce(currentQuestion.suffix, "");
  currentQuestion[TEMP_CHOICE_EXCLUSIVE] = choice[CHOICE_EXCLUSIVE];
  currentQuestion[TEMP_OTHER_OPTION] = choice[OTHER_OPTION];
  currentQuestion[TEMP_CHOICE_TYPE] = choice[TYPE_KEY];
  currentQuestion[TEMP_CHOICE_TEXT] = choice.text;
  currentQuestion[PREDEFINED_LIST_TYPE] = coalesce(choice.predefinedListType, "regions");
  currentQuestion.tempValidators = coalesce(currentQuestion.validators, []);
}

function handleConfirmSubchoiceAction(currentState, action) {
  let currentQuestion = getCurrentQuestion(currentState, action);
  currentQuestion.editSubchoice = null;
  const target = action.choiceTarget;
  const choiceName = action.choiceName;
  let choice = ObjectUtils.findByObjectProp(currentQuestion[target], NAME_KEY, choiceName);
  choice.choices = coalesce(currentQuestion.tempChoices, []);
  choice.suffix = coalesce(currentQuestion[TEMP_SUFFIX], "");
  choice[CHOICE_EXCLUSIVE] = currentQuestion[TEMP_CHOICE_EXCLUSIVE];
  choice[OTHER_OPTION] = currentQuestion[TEMP_OTHER_OPTION];
  choice[TYPE_KEY] = currentQuestion[TEMP_CHOICE_TYPE];
  choice[PREDEFINED_LIST_TYPE] = currentQuestion[PREDEFINED_LIST_TYPE];
  currentQuestion[PREDEFINED_LIST_TYPE] = null;
  choice.text = currentQuestion[TEMP_CHOICE_TEXT];
  currentQuestion.validators = coalesce(currentQuestion.tempValidators, []);
}

function handleCancelSubchoiceAction(currentState, action) {
  let currentQuestion = getCurrentQuestion(currentState, action);
  currentQuestion.editSubchoice = null;
}

function handleRemoveQuestionChoiceAction(currentState, action) {
  let currentQuestion = getCurrentQuestion(currentState, action);
  let choiceTarget = coalesce(action.choiceTarget, "choices");
  let minCount =
    currentQuestion.type === "dynamicTable" ||
    currentQuestion.type === "staticTable" ||
    currentQuestion.type === "checkbox" ||
    currentQuestion.type === "matrixMultipleChoice" ||
    currentQuestion.type === "matrixSingleChoice"
      ? 1
      : 2;

  if (currentQuestion[choiceTarget].length <= minCount) {
    console.warn(`Cannot have less than ${minCount} choices.`);
  } else{
    currentQuestion[choiceTarget] = ObjectUtils.removeObjectByProp(
        currentQuestion[choiceTarget],
        [NAME_KEY],
        action.choiceName
      );
  }


}

function getCurrentPage(currentState, action) {
  let allPages = currentState[PAGES_KEY];
  let pageName = action[PAGE_NAME];

  return ObjectUtils.findByObjectProp(allPages, [NAME_KEY], pageName);
}

function getCurrentQuestion(currentState, action) {
  let questionName = action[NAME_KEY];
  let currentPage = getCurrentPage(currentState, action);

  return ObjectUtils.findByObjectProp(currentPage.questions, [NAME_KEY], questionName);
}

function getCurrentChoice(currentState, action) {
  let choiceName = action[CHOICE_NAME];
  let currentQuestion = getCurrentQuestion(currentState, action);
  let choiceDescription = action[CHOICE_DESCRIPTION];
  let choiceTarget = coalesce(action[CHOICE_TARGET], "choices");
  let result = ObjectUtils.findByObjectProp(currentQuestion[choiceTarget], [NAME_KEY], choiceName, choiceDescription);

  if (action[CHOICE_CHOICE_NAME]) {
    return ObjectUtils.findByObjectProp(result["choices"], [NAME_KEY], action[CHOICE_CHOICE_NAME], choiceDescription);
  }

  if (!result[NAME_KEY]) {
    //backward compatibility
    result = ObjectUtils.findByObjectProp(currentQuestion.choices, "value", choiceName, choiceDescription);
  }
  return result;
}

function handleChangeOfIndicatorInQuestion(currentState, action, addNew) {
  let currentQuestion = getCurrentQuestion(currentState, action);
  //get indicator object by its id
  let indicator = ObjectUtils.findByObjectProp(currentState.allIndicators, "identifier", action.fieldValue);
  //if the action is to add a new indicator to indicators- we add new one in array, if not we are removing it
  if (addNew) {
    //if indicator is replaced by another
    if (action[INDICATOR_TO_BE_REPLACED]) {
      let index = -1; //ObjectUtils.findObjectIndexInArray(currentQuestion[INDICATORS], "id", action[INDICATOR_TO_BE_REPLACED]);
      for (let i = 0; i < currentQuestion[INDICATORS].length; i++) {
        if (currentQuestion[INDICATORS][i]["identifier"] === action[INDICATOR_TO_BE_REPLACED]) {
          index = i;
        }
      }
      if (indicator.identifier) {
        currentQuestion[INDICATORS][index] = {};
        currentQuestion[INDICATORS][index] = indicator;
      } else {
        currentQuestion[INDICATORS] = ObjectUtils.removeObjectByProp(
          currentQuestion[INDICATORS],
          "identifier",
          action[INDICATOR_TO_BE_REPLACED]
        );
      }
    }
    //totally new one
    else {
      if (!currentQuestion[INDICATORS]) {
        currentQuestion[INDICATORS] = [];
      }
      currentQuestion[INDICATORS].push(indicator);
    }
  }
  //remove
  else {
    currentQuestion[INDICATORS] = ObjectUtils.removeObjectByProp(
      currentQuestion[INDICATORS],
      "identifier",
      action.fieldValue
    );
  }
}
